import { IconButton, Stack, useTheme } from "@mui/material";
import {
	Ellipsis,
	Info,
	MessageCircle,
	NotebookPen,
	UsersRound,
} from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { ActivityParticipants } from "web_myplann/Activities/Components/ActivityParticipants";
import { getAgentsWithCreator } from "web_myplann/Activities/Helpers/getAgentsWithCreator";
import { useFormatDateTimeByClientActivity } from "web_myplann/Activities/Hooks/useActivitiesFormatDateAndTimes";
import { useActivityMenuItems } from "web_myplann/Activities/Hooks/useActivityMenuItems";
import { useActivityAgentsQuery } from "web_myplann/Api/Activities/useActivityAgentsQuery";
import { useActivityNotesQuery } from "web_myplann/Api/Activities/useActivityNotesQuery";
import { useActivityParticipantsQuery } from "web_myplann/Api/Activities/useActivityParticipantsQuery";
import { useActivityQuery } from "web_myplann/Api/Activities/useActivityQuery";
import { useAgentQuery } from "web_myplann/Api/Advisor/useAgentQuery";
import { useAssignedAgentsQuery } from "web_myplann/Api/Advisor/useAssignedAgentsQuery";
import { UserAgentStatusEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { DataCard } from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { ToolBarMenu } from "web_myplann/Shared/Components/ToolBarMenu";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { DEFAULT_ROUTE } from "web_myplann/Utils/constants";
import { device } from "web_myplann/Utils/device";
import { getMapsLink } from "web_myplann/Utils/getMapsLink";

export const ActivityDetailPage = () => {
	const { id } = useParams<{ id: string }>();
	const theme = useTheme();
	const { t } = useResource();
	const formatDateTime = useFormatDateTimeByClientActivity();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const { data: dataEvent, isFetching: isFetchingEvent } = useActivityQuery(
		Number(id),
	);
	const { data: dataAgents, isFetching: isFetchingAgents } =
		useActivityAgentsQuery(Number(id));
	const { data: dataNotes, isFetching: isFetchingNotes } =
		useActivityNotesQuery(Number(id));
	const { data: dataParticipants, isFetching: isFetchingParticipants } =
		useActivityParticipantsQuery(Number(id));

	const { data: agentDetail } = useAgentQuery(dataEvent?.author?.id ?? 0);
	const { data: assignedAgents } = useAssignedAgentsQuery(
		UserAgentStatusEnum.Connected,
	);

	const activityType = dataEvent?.type
		? t(Resources.Activity.List.ActivityType[dataEvent.type])
		: "";

	const menuItems = useActivityMenuItems(agentDetail);

	const agents = getAgentsWithCreator(
		dataAgents?.data ?? [],
		dataEvent?.creator,
	);

	const encodedAddress = encodeURIComponent(dataEvent?.place ?? "");
	const mapsUrl = getMapsLink(encodedAddress);

	return (
		<>
			<MyPlannLayout
				topBar={
					<NavigationHeader
						title={t(Resources.Activity.Detail.title)}
						defaultRoute={DEFAULT_ROUTE}
						endComponent={
							<Stack direction="row" gap={1.6}>
								<IconButton
									onClick={() => {
										device.navigateChatRequest(
											String(agentDetail?.conversationId ?? ""),
											agentDetail?.conversationTitle ?? agentDetail?.name ?? "",
											agentDetail?.uid,
										);
									}}
									sx={{ padding: 0 }}
								>
									<MessageCircle
										cursor="pointer"
										color={theme.palette.primary.main}
									/>
								</IconButton>
								<IconButton
									sx={{ padding: 0 }}
									onClick={(event) => {
										event.stopPropagation();
										setAnchorEl(event.currentTarget);
									}}
									disableFocusRipple
									disableTouchRipple
								>
									<Ellipsis
										cursor="pointer"
										color={theme.palette.primary.main}
									/>
								</IconButton>
							</Stack>
						}
					/>
				}
			>
				{isFetchingEvent ||
				isFetchingNotes ||
				isFetchingParticipants ||
				isFetchingAgents ? (
					<DetailSectionLoader />
				) : (
					dataEvent && (
						<Stack paddingTop={3.2}>
							<DataCard
								icon={<Info size={16} color={theme.palette.grey[700]} />}
								label={t(Resources.Activity.Detail.BasicInfo.title)}
							>
								{dataEvent?.title && (
									<DataRow
										label={t(Resources.Activity.Detail.BasicInfo.Name)}
										value={dataEvent?.title}
									/>
								)}
								<DataRow
									label={t(Resources.Activity.Detail.BasicInfo.Type)}
									value={activityType}
								/>
								<DataRow
									label={t(Resources.Activity.Detail.BasicInfo.Date)}
									value={formatDateTime(dataEvent)}
								/>
								<DataRow
									label={t(Resources.Activity.Detail.BasicInfo.Address)}
									value={dataEvent.place}
									link={mapsUrl}
								/>
								{assignedAgents?.data.length &&
									assignedAgents?.data.length > 1 && (
										<DataRow
											label={t(Resources.Activity.Detail.BasicInfo.CreatedBy)}
											value={`${dataEvent.author.name} (${t(Resources.Dashboard.UserType[dataEvent.author.type])})`}
										/>
									)}
							</DataCard>

							<DataCard
								icon={<UsersRound size={16} />}
								label={t(Resources.Activity.Detail.Participants.title)}
								noDivider={dataNotes?.data.length === 0}
							>
								<ActivityParticipants
									participants={dataParticipants?.data ?? []}
									agents={agents}
								/>
							</DataCard>

							<DataCard
								icon={<NotebookPen size={16} />}
								label={t(Resources.Activity.Detail.notes)}
								noDivider
							>
								<Stack direction="column" gap={0.8}>
									{dataNotes?.data.map((n) => (
										<DataNote key={n.id} label={n.title} text={n.text} />
									))}
								</Stack>
							</DataCard>
						</Stack>
					)
				)}
			</MyPlannLayout>
			<ToolBarMenu
				anchorEl={anchorEl}
				setAnchorEl={setAnchorEl}
				items={menuItems}
			/>
		</>
	);
};
